import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, ReportSummaryStrip, reportMeta } from "./Frame";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusPill, SubHeading, plural, shortDate } from "../kit";

/**
 * Report — Back-ordered items. "What did we promise that has not arrived?"
 *
 * Longest wait first, because the oldest promise is the one a clinic phones
 * about. The wait is the only figure on the right-hand rail and it never wraps.
 *
 * Every row carries the pill rather than a bare red row, because Gloria left
 * back-order colour open — "red *or* separate back-order status". `StatusPill`
 * with the default `kind="line"` is the one presenter that honours
 * `backOrderUsesOwnColour`, so request-sourced and dispatch-sourced rows both
 * use it: one concept, one colour, whichever she picks at the demo.
 *
 * 390px first: the item cell wraps rather than truncating and the wait column
 * never does, and the registry's table container is what scrolls if the two
 * together outgrow the phone — the page body still does not.
 */
export function BackOrdered() {
  const data = useQuery(api.reports.backOrdered.data);

  return (
    <ReportFrame
      {...reportMeta("back-ordered")}
      question="What did we promise that has not arrived?"
      data={data}
      count={data?.rows.length}
      empty={{
        title: "Nothing on back-order",
        body: "Every line a clinic asked for has either shipped or been received. Nothing is stuck with a vendor.",
        good: true,
      }}
      footnote="Counted from the day the line was marked back-ordered — both lines a manager back-ordered and lines a clinic reported short on delivery."
    >
      {data ? (
        <>
          <ReportSummaryStrip>
            <span className="num font-semibold">
              {data.totalLines} {plural(data.totalLines, "line")} waiting
            </span>
            <span className="num">
              {data.totalUnits} {plural(data.totalUnits, "unit")}
            </span>
            <span className="num">
              {data.clinicsAffected} {plural(data.clinicsAffected, "clinic")}
            </span>
            <span className="num">longest {data.oldestWaitedLabel}</span>
          </ReportSummaryStrip>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Waiting</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell className="whitespace-normal">
                    <div className="font-medium">{row.itemName}</div>
                    <div className="text-xs text-muted-foreground">
                      {row.clinicName} · {row.qty}
                      {row.packSize ? ` × ${row.packSize}` : ""}
                    </div>

                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                      <StatusPill
                        status="back-ordered"
                        backOrderUsesOwnColour={data.backOrderUsesOwnColour}
                      />
                      {row.vendor ? (
                        <span className="text-xs text-muted-foreground">
                          {row.vendor}
                        </span>
                      ) : null}
                      {row.dispatchNumber ? (
                        <span className="text-xs text-muted-foreground">
                          short on {row.dispatchNumber}
                        </span>
                      ) : null}
                    </div>

                    {row.vendorNote ? (
                      <p className="mt-1.5 rounded-md bg-muted px-2 py-1 text-xs break-words">
                        {row.vendorNote}
                      </p>
                    ) : null}
                    {row.receiptNote ? (
                      <p className="mt-1 text-xs break-words text-muted-foreground italic">
                        {row.receiptNote}
                      </p>
                    ) : null}
                  </TableCell>

                  <TableCell className="num text-right align-top">
                    <div className="font-medium">{row.waitedLabel}</div>
                    <div className="text-xs text-muted-foreground">
                      since {shortDate(row.sinceAt)}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {data.byClinic.length > 1 ? (
            <div className="mt-4">
              <SubHeading as="h4" className="mb-1">By clinic</SubHeading>
              <Table>
                <TableBody>
                  {data.byClinic.map((clinic) => (
                    <TableRow key={clinic.clinicId}>
                      <TableCell className="whitespace-normal">
                        <div className="font-medium">{clinic.clinicName}</div>
                        <div className="text-xs text-muted-foreground">
                          longest wait {clinic.oldestWaitedLabel}
                        </div>
                      </TableCell>
                      <TableCell className="num text-right font-medium">
                        {clinic.lines} {plural(clinic.lines, "line")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : null}
        </>
      ) : null}
    </ReportFrame>
  );
}
